import React, { useEffect } from "react";
import PageLayout from "@/components/layout/pageLayout/PageLayout";
import DashboardSidebar from "../dashboard/DashboardSidebar";
import DashboardMain from "../dashboard/DashboardMain";
import { useDashboard } from "../dashboard/useDashboard";
import AlertMessage from "@/components/layout/message/AlertMessage";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { AppRoutes } from "@/routes/routeMap";
import AppHeader from "@/components/layout/header/AppHeader";

const NAV_ITEMS = [
    { id: "results", label: "Resultados por prueba", icon: "📊" },
    { id: "comparative", label: "Análisis comparativo", icon: "📈" },
    { id: "competences", label: "Competencias", icon: "🏆" },
    { id: "thematic-areas", label: "Áreas temáticas", icon: "📊" },
    { id: "historical", label: "Histórico", icon: "📅" },
    { id: "recommendations", label: "Recomendaciones", icon: "📊" },
];

const StudentDashboard: React.FC = () => {
    const location = useLocation();
    const { user } = useAuth();

    useEffect(() => {
        document.body.classList.add("solid-public-bg");
        return () => document.body.classList.remove("solid-public-bg");
    }, []);

    const studentIdFromUrl = new URLSearchParams(window.location.search).get("studentId");
    const isViewingAsOther = (user?.role === "Facilitador" || user?.role === "Coordinador de curso") && studentIdFromUrl;

    let backPath: string | undefined;
    if (isViewingAsOther) {
        backPath = location.state?.from || "/facilitator-panel/user-management";
    } else if (user?.role === "Estudiante") {
        backPath = AppRoutes.STUDENT_HOME;
    }

    const {
        simulations,
        dashboardData,
        selectedSimulation,
        activeSection,
        isLoading,
        error,
        hasData,
        handleSimulationChange,
        handleNavigation,
        handleSaveRecommendations,
        canEditRecommendations,
    } = useDashboard({ studentId: studentIdFromUrl });

    if (!isLoading && !error && simulations.length === 0) {
        return (
            <PageLayout
                header={
                    <AppHeader
                        simulations={simulations}
                        selectedSimulationId={selectedSimulation?.id || ""}
                        onSimulationChange={handleSimulationChange}
                        isLoading={isLoading}
                        backButtonPath={backPath}
                    />
                }
                main={
                    <div style={{ padding: "2rem" }}>
                        <AlertMessage type="info" title="No hay simulacros asignados">
                            Este estudiante aún no ha sido asignado a ningún simulacro. Por
                            favor, asigne uno desde el panel de gestión de usuarios para ver
                            los resultados.
                        </AlertMessage>
                    </div>
                }
            />
        );
    }

    return (
        <PageLayout
            header={
                <AppHeader
                    simulations={simulations}
                    selectedSimulationId={selectedSimulation?.id || ""}
                    onSimulationChange={handleSimulationChange}
                    isLoading={isLoading}
                    backButtonPath={backPath}
                />
            }
            sidebar={
                hasData ? (
                    <DashboardSidebar
                        selectedSimulation={selectedSimulation}
                        activeSection={activeSection}
                        navItems={NAV_ITEMS}
                        onNavigate={handleNavigation}
                        globalScore={dashboardData?.summary.score || 0}
                        percentile={dashboardData?.summary.percentile || 0}
                    />
                ) : null
            }
            main={
                !hasData && !isLoading ? (
                    <div style={{ padding: "2rem" }}>
                        <AlertMessage type="info" title="Resultados aún no disponibles">
                            Los resultados para esta simulación aún no han sido procesados.
                            Por favor, inténtelo de nuevo más tarde.
                        </AlertMessage>
                    </div>
                ) : (
                    <DashboardMain
                        isLoading={isLoading}
                        error={error}
                        dashboardData={dashboardData}
                        simulations={simulations}
                        canEditRecommendations={canEditRecommendations}
                        onSaveRecommendations={handleSaveRecommendations}
                    />
                )
            }
        />
    );
};

export default StudentDashboard;
